import NonConsolidationDomestic from '@components/common/quote-wrapper/components/domestic/components/non-consolidation-domestic';
import css from './index.module.scss';
import { Button } from '@components/base';
import {
  DOMESTIC_NON_CONSOLIDATION_ENQUIRY_QUOTE_DEFAULT_VALUE,
  toQuoteProductOption,
  getCountryOfOriginOption,
  getMaterialLocationCountryOption
} from '@components/common/quote-wrapper/components/helper';
import { CURRENCY, ENQUIRY_TYPE, OPERATION_TYPE } from '@helpers/constants';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useEffect } from 'react';
import { IFilterState, IWeightChildOption } from '@helpers/types/quotes';
import CostCalculator from '@components/common/quote-wrapper/components/cost-calculator';
import { yupResolver } from '@hookform/resolvers/yup';
import { domesticNonConsolidationAddEditQuoteValidation } from '@helpers/yup/add-quote.schema';
import { notifyFirstFormError } from '@helpers/utils';

interface IDomesticNonConsolidation {
  quoteFormState: any;
  filterState: IFilterState;
  filterProductOptions: (event: any, param?: any) => Promise<void>;
  onClose: () => void;
  onFormSubmit: (enquiryQuote: any) => Promise<void>;
  data?: any;
}

const DomesticNonConsolidation = (props: IDomesticNonConsolidation) => {
  const { quoteFormState, filterState, filterProductOptions, onClose, onFormSubmit, data } = props;

  const {
    selectedWeightSystemChildOptions,
    selectedCurrency,
    productOptions,
    isEdit,
    primaryUnit,
    currency1
  } = quoteFormState;

  const form = useForm<any>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(domesticNonConsolidationAddEditQuoteValidation),
    defaultValues: {
      quote: [DOMESTIC_NON_CONSOLIDATION_ENQUIRY_QUOTE_DEFAULT_VALUE]
    }
  });

  const { control, handleSubmit } = form;

  const {
    fields: fields,
    append: append,
    remove: remove
  } = useFieldArray({
    name: 'quote',
    control
  });

  const quoteWatch = useWatch({
    control,
    name: 'quote'
  });

  useEffect(() => {
    if (data) {
      const productName = toQuoteProductOption(data?.product_data ?? data);

      form.reset({
        quote: [
          {
            quote_id: data.quote_id,
            isExpanded: true,
            productName: productName,
            productPackaging: data?.packaging,
            dealQuantity: data?.quantity,
            dealQuantityUnit: data?.quantity_unit
              ? selectedWeightSystemChildOptions?.find(
                  (i: IWeightChildOption) => i.value === data.quantity_unit
                )
              : primaryUnit,
            base_price: {
              value: data.base_price ? data.base_price?.value : 0,
              currency: data?.base_price
                ? CURRENCY.find((i) => i.value === data?.base_price?.currency)
                : currency1,
              cost_id: isEdit == OPERATION_TYPE.EDIT ? data.base_price?.cost_id : null,
              manualOverride: true,
              weightUnit: data?.base_price?.unit_of_weight
                ? selectedWeightSystemChildOptions?.find(
                    (i: IWeightChildOption) => i.value === data?.base_price?.unit_of_weight
                  )
                : primaryUnit
            },
            moq: data?.moq,
            moqUnit: data?.moq_unit
              ? selectedWeightSystemChildOptions?.find(
                  (i: IWeightChildOption) => i.value === data?.moq_unit
                )
              : primaryUnit,
            totalWeight: data?.weight_one_container,
            totalWeightUnit: data?.weight_one_container_unit
              ? selectedWeightSystemChildOptions?.find(
                  (item: IWeightChildOption) => item.value === data?.weight_one_container_unit
                )
              : primaryUnit,
            totalLocalFreight: {
              value: data.total_local_freight ? data.total_local_freight?.value : 0,
              currency: data.total_local_freight
                ? CURRENCY.find((item) => item.value === data?.total_local_freight?.currency)
                : currency1,
              cost_id: isEdit == OPERATION_TYPE.EDIT ? data.total_local_freight?.cost_id : null
            },
            incoTermsLocation: data?.deal_incoterm_location,
            additionalCost: {
              value: data.additional_cost ? data.additional_cost?.value : 0,
              currency: data?.additional_cost
                ? CURRENCY.find((item) => item?.value === data?.additional_cost?.currency)
                : currency1,
              cost_id: isEdit == OPERATION_TYPE.EDIT ? data.additional_cost?.cost_id : null
            },
            insuranceCost: data?.insurance_cost,
            financeCost: data?.finance_cost,
            profitMarginExpected: data?.profit_margin,
            discount: data?.discount,
            finalQuote_selling_price: {
              value: data.final_price ? data.final_price?.value : 0,
              currency: data?.final_price
                ? CURRENCY.find((item) => item.value === data?.final_price?.currency)
                : currency1,
              cost_id: isEdit == OPERATION_TYPE.EDIT ? data?.final_price?.cost_id : null,
              weightUnit: data?.final_price?.unit_of_weight
                ? selectedWeightSystemChildOptions?.find(
                    (item: IWeightChildOption) => item.value === data?.final_price?.unit_of_weight
                  )
                : primaryUnit,
              manualOverride: true
            },
            countryOfOrigin: getCountryOfOriginOption(data?.origin, data?.origin_display_value),
            materialLocationCountry: getMaterialLocationCountryOption(
              data?.material_location_country,
              data?.material_location_country_display_value
            ),
            incoTermsZipCode: data.zip_code,
            warehousingCost: {
              value: data.warehousing_cost ? data.warehousing_cost?.value : 0,
              currency: data?.warehousing_cost
                ? CURRENCY.find((item) => item.value === data?.warehousing_cost?.currency)
                : currency1,
              cost_id: isEdit == OPERATION_TYPE.EDIT ? data?.warehousing_cost?.cost_id : null
            }
          }
        ]
      });
    }
  }, [data]);

  return (
    <form noValidate onSubmit={handleSubmit(onFormSubmit, notifyFirstFormError)}>
      <FormProvider {...form}>
        <CostCalculator
          filters={filterState}
          weightOptions={selectedWeightSystemChildOptions}
          currency={currency1}
          primaryUnitWatch={primaryUnit}
          quote={quoteWatch}
          enquiryType={ENQUIRY_TYPE.DOMESTIC}
        />

        {fields.map((quote: any, index: number) => {
          return (
            <NonConsolidationDomestic
              key={index}
              index={index}
              isEnquiry={true}
              fieldNames={{
                isExpanded: `quote.${index}.isExpanded`,
                productName: `quote.${index}.productName`,
                productPackaging: `quote.${index}.productPackaging`,
                dealQuantity: `quote.${index}.dealQuantity`,
                dealQuantityUnit: `quote.${index}.dealQuantityUnit`,
                moq: `quote.${index}.moq`,
                moqUnit: `quote.${index}.moqUnit`,
                incoTermsZipCode: `quote.${index}.incoTermsZipCode`,
                incoTermsLocation: `quote.${index}.incoTermsLocation`,
                countryOfOrigin: `quote.${index}.countryOfOrigin`,
                materialLocationCountry: `quote.${index}.materialLocationCountry`,
                totalWeight: `quote.${index}.totalWeight`,
                totalWeightUnit: `quote.${index}.totalWeightUnit`,
                totalLocalFreightPrice: `quote.${index}.totalLocalFreight.value`,
                totalLocalFreightCurrency: `quote.${index}.totalLocalFreight.currency`,
                basePrice: `quote.${index}.base_price.value`,
                basePriceCurrency: `quote.${index}.base_price.currency`,
                basePriceWeightUnit: `quote.${index}.base_price.weightUnit`,
                warehousingCostPrice: `quote.${index}.warehousingCost.value`,
                warehousingCostCurrency: `quote.${index}.warehousingCost.currency`,
                additionalCostPrice: `quote.${index}.additionalCost.value`,
                additionalCostCurrency: `quote.${index}.additionalCost.currency`,
                financeCost: `quote.${index}.financeCost`,
                profitMarginExpected: `quote.${index}.profitMarginExpected`,
                discount: `quote.${index}.discount`,
                insuranceCost: `quote.${index}.insuranceCost`,
                finalQuoteSellingPrice: `quote.${index}.finalQuote_selling_price.value`,
                finalQuoteSellingPriceCurrency: `quote.${index}.finalQuote_selling_price.currency`,
                finalQuoteSellingPriceWeightUnit: `quote.${index}.finalQuote_selling_price.weightUnit`
              }}
              fieldLength={fields.length}
              filterProductOptions={filterProductOptions}
              productOptions={productOptions}
              selectedWeightSystemChildOptions={selectedWeightSystemChildOptions}
              selectedCurrency={selectedCurrency}
              primaryUnitWatch={primaryUnit}
              currency1Watch={currency1}
              disableProductName={isEdit === OPERATION_TYPE.ADD_PRODUCT ? false : true}
              enableZipcodeAndPlaceOfDischarge={true}
            />
          );
        })}

        <section className={css.actionButtons}>
          <Button variant="outlined-secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" type="submit">
            {isEdit == OPERATION_TYPE.EDIT ? 'Update Quote' : 'Create Quote'}
          </Button>
        </section>
      </FormProvider>
    </form>
  );
};

export default DomesticNonConsolidation;
